import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Dna,
  Landmark,
  Map,
  MapPinned,
  Radar,
  RotateCcw,
  Route,
  ScrollText,
  Trees,
} from 'lucide-react';

const investigations = [
  {
    id: 'colours-of-empire-roman-mosaics',
    label: 'Featured Investigation',
    title: 'The Colours of Empire',
    date: 'Roman Britain · 2nd–5th centuries',
    description:
      'Stone, skill and status in Roman Britain’s mosaics. Explore Chedworth alongside Corinium, Leicester, Silchester and Badminton Park, tracing the stones, durability and cost.',
    href: '/investigations/colours-of-empire-roman-mosaics.html',
    tags: ['Roman mosaics', 'Chedworth', 'Stone provenance', 'Craftsmanship'],
    visualTitle: 'The Colours of Empire',
    visualSubtitle: 'Stone · Skill · Status · Evidence',
    buttonLabel: 'Explore the mosaics',
    icon: 'landmark',
  },
  {
    id: 'two-fires-midwinter',
    label: 'Evidence-Led Investigation',
    title: 'Two Fires at Midwinter',
    date: 'c. 2500 BC–Roman Britain',
    description:
      'Set Saturnalia beside Britain’s deeper midwinter evidence and test what Roman texts, military calendars, Celtic timekeeping and archaeology really allow us to say.',
    href: '/investigations/two-fires-at-midwinter.html',
    tags: ['Saturnalia', 'Midwinter', 'Coligny Calendar', 'How we know'],
    visualTitle: 'Two Fires at Midwinter',
    visualSubtitle: 'Saturnalia · Solstice · Calendars · Evidence',
    buttonLabel: 'Enter the winter',
    icon: 'trees',
  },
  {
    id: 'barbegal-machine-landscape',
    label: 'Engineering Investigation',
    title: 'Barbegal: How Do You Build a Machine Out of a Landscape?',
    date: 'c. AD 120–130 · Gallia Narbonensis',
    description:
      'Follow the water across the hillside, pivot at the headworks and descend through Barbegal’s sixteen mills.',
    href: '/investigations/barbegal-machine-landscape.html',
    tags: ['Barbegal', 'Hydraulics', 'Aqueducts', 'Roman engineering'],
    visualTitle: 'Follow the Water',
    visualSubtitle: 'Aqueduct · Pivot · Mills · Evidence',
    buttonLabel: 'Follow the water',
    icon: 'landmark',
  },
  {
    id: 'after-twenty-five-years',
    label: 'Evidence-Led Investigation',
    title: 'After Twenty-Five Years',
    date: 'AD 103–105 · Roman Cheshire',
    description:
      'Two cavalry diplomas, only two years apart. Follow Reburrus and the Middlewich soldier into Cheshire’s military, salt-working and Cornovian landscape.',
    href: '/investigations/the-farm-of-reburrus.html',
    tags: ['Military diplomas', 'Cheshire', 'Salt', 'How we know'],
    visualTitle: 'After Twenty-Five Years',
    visualSubtitle: 'Bickley · Middlewich · Salt · Settlement',
    buttonLabel: 'Follow the evidence',
    icon: 'inscription',
  },
  {
    id: 'road-before-watling-street',
    label: 'Evidence-Led Investigation',
    title: 'The Road Before Watling Street',
    date: 'Middle Iron Age–Roman Britain',
    description:
      'Follow a claimed Iron Age route through Brewood and test it against excavation, historic sources and the Roman road network around Pennocrucium.',
    href: '/investigations/the-road-before-watling-street.html',
    tags: ['Brewood', 'Iron Age', 'Roman roads', 'How we know'],
    visualTitle: 'The Road Before Watling Street',
    visualSubtitle: 'Brewood · Claim · Evidence · Test',
    buttonLabel: 'Test the road',
    icon: 'route',
  },
  {
    id: 'missing-villas-western-britain',
    label: 'Evidence-Led Investigation',
    title: 'The Missing Villas of Western Britain',
    date: 'Roman Britain · 1st–4th centuries',
    description:
      'Compare secure Engleton with ambiguous Ffrith and test whether the sparse western villa map reflects ancient settlement, archaeology, or classification.',
    href: '/investigations/the-missing-villas-of-western-britain.html',
    tags: ['Roman villas', 'Ffrith', 'Engleton', 'How we know'],
    visualTitle: 'The Missing Villas',
    visualSubtitle: 'Engleton · Ffrith · Classification · Absence',
    buttonLabel: 'Test the villa map',
    icon: 'landmark',
  },
  {
    id: 'silures-hidden-defeat',
    label: 'Featured Investigation',
    title: "Rome's Hidden Defeat: The Silures, Valens and Gloucester",
    date: 'c. AD 52',
    description:
      'Test the claim that the Silures stormed Gloucester. Follow Tacitus, Kingsholm archaeology, and legionary evidence to secure event from later reconstruction.',
    href: '/investigations/romes-hidden-defeat-silures-ad52.html',
    tags: ['Silures', 'Manlius Valens', 'Kingsholm', 'Evidence audit'],
    visualTitle: 'A Victory Without a Battlefield',
    visualSubtitle: 'Silures · Valens · c. AD 52',
    buttonLabel: 'Reconstruct the defeat',
    icon: 'landmark',
  },
  {
    id: 'agricola-mona',
    label: 'Featured Investigation',
    title: 'Agricola and the Druids of Mona',
    date: '77–78 CE',
    description:
      'Follow Agricola’s campaign through Tacitus, archaeology, Welsh place names and the contested crossing of the Menai Strait.',
    href: '/investigations/agricola-campaign-mona.html',
    tags: ['Agricola', 'Mona', 'Archaeology', '20-minute read'],
    visualTitle: 'The Menai Strait',
    visualSubtitle: 'Ynys Môn · 77–78 CE',
    buttonLabel: 'Explore the campaign',
    icon: 'map',
  },
  {
    id: 'fortified-landscapes-iron-age-britain',
    label: 'Interactive Landscape Investigation',
    title: 'The Fortified Landscapes of Iron Age Britain',
    date: 'Iron Age Britain · Hillforts and oppida',
    description:
      'Choose a place, reveal the sites around it, and explore outward through thousands of hillfort and oppidum records across Britain.',
    href: '/investigations/romancelticmap.html',
    tags: ['Hillforts', 'Oppida', 'Landscape', 'Interactive map'],
    visualTitle: 'The Fortified Landscapes',
    visualSubtitle: 'Hillforts · Oppida · Place · Discovery',
    buttonLabel: 'Explore the landscape',
    icon: 'map-pinned',
  },
  {
    id: 'people-beneath-landscape',
    label: 'Interactive Investigation',
    title: 'The People Beneath the Landscape',
    date: 'Prehistory–Viking Age',
    description:
      'Enter a town or postcode to explore the regional population history revealed by genetics, ancient DNA, archaeology and historical evidence.',
    href: '/investigations/people-beneath-landscape.html',
    tags: ['Genetics', 'Ancient DNA', 'Migration', 'Interactive map'],
    visualTitle: 'Who Lived Here?',
    visualSubtitle: 'England · Wales · Borderlands',
    buttonLabel: 'Investigate a location',
    icon: 'dna',
  },
  {
    id: 'ghost-roads',
    label: 'Interactive Investigation',
    title: 'Ghost Roads',
    date: 'Roman Britain · Revealed by LiDAR',
    description:
      'Follow a guided visual investigation showing how LiDAR reveals hidden roads, why some lines mislead and how archaeologists weigh the evidence.',
    href: '/investigations/ghost-roads.html',
    tags: ['LiDAR', 'Roman Roads', 'Visual guide', 'No prior knowledge'],
    visualTitle: 'The Laser and the Legion',
    visualSubtitle: 'Surface · Terrain · Evidence',
    buttonLabel: 'Reveal the ghost roads',
    icon: 'route',
  },
  {
    id: 'when-landscape-goes-silent',
    label: 'Guided Investigation',
    title: 'When the Landscape Goes Silent',
    date: 'Roman roads · Crops and radar',
    description:
      'Continue beyond LiDAR to discover how crop stress, near-infrared imagery and ground-penetrating radar reveal roads with no surviving surface trace.',
    href: '/investigations/when-landscape-goes-silent.html',
    tags: ['Cropmarks', 'Multispectral', 'GPR', 'Visual guide'],
    visualTitle: 'Above and Below',
    visualSubtitle: 'Crop · Spectrum · Radar',
    buttonLabel: 'See beneath the field',
    icon: 'radar',
  },
  {
    id: 'where-sacred-groves-stood',
    label: 'Map Investigation',
    title: 'Where the Sacred Groves Once Stood',
    date: 'Prehistory–Roman Britain',
    description:
      'Map proposed nemeta within Britain’s deeper ritual landscape, separating ancient testimony, place-name evidence, archaeology and informed interpretation.',
    href: '/investigations/where-sacred-groves-stood.html',
    tags: ['Sacred landscapes', 'Place names', 'Interactive map', 'Evidence levels'],
    visualTitle: 'The Names the Groves Left',
    visualSubtitle: 'Mona · Nymet · Vernemeton',
    buttonLabel: 'Reveal the sacred landscapes',
    icon: 'trees',
  },
  {
    id: 'church-brough-inscription-edge',
    label: 'Epigraphic Investigation',
    title: 'Church Brough — Inscription at the Edge',
    date: 'AD 197–3rd century',
    description:
      'Enter the Stainmore Pass and follow two inscriptions from imperial authority to the Greek verse epitaph of Hermes of Commagene.',
    href: '/investigations/church-brough-inscription-at-the-edge.html',
    tags: ['Greek inscription', 'Verteris', 'Identity', 'Evidence revision'],
    visualTitle: 'Two Voices in Stone',
    visualSubtitle: 'Empire · Boy · Pass · Memory',
    buttonLabel: 'Read the inscriptions',
    icon: 'inscription',
  },
  {
    id: 'roman-britain-beneath-your-feet',
    label: 'Interactive Map Investigation',
    title: 'Roman Britain Beneath Your Feet',
    date: 'Roman Britain · Local landscape search',
    description:
      'Enter a town or postcode to reveal the nearest Roman road corridor, military site, settlement and broad reconstructed tribal landscape.',
    href: '/investigations/roman-britain-beneath-your-feet.html',
    tags: ['Local history', 'Roman roads', 'Archaeology', 'Interactive map'],
    visualTitle: 'What Did Rome Build Here?',
    visualSubtitle: 'Road · Fort · Town · Territory',
    buttonLabel: 'Investigate a location',
    icon: 'map-pinned',
  },
  {
    id: 'walls-and-power-stones-outlived-rome',
    label: 'Two-Part Investigation',
    title: 'Walls and Power: The Stones That Outlived Rome',
    date: 'Roman Britain–early medieval landscape',
    description:
      'Discover why Roman towns raised walls to defend, display and control—then follow the same stone into timber halls, church walls, gateposts and baptismal fonts.',
    href: '/investigations/walls-and-power-stones-outlived-rome.html',
    tags: ['Town walls', 'Cunetio', 'Wroxeter', 'Stone reuse'],
    visualTitle: 'From Fortress to Font',
    visualSubtitle: 'Function · Collapse · Repurpose',
    buttonLabel: 'Follow the stones',
    icon: 'landmark',
  },
  {
    id: 'sea-roads-roman-britain',
    label: 'Featured Investigation',
    title: 'The Sea Roads of Roman Britain',
    date: 'Roman conquest–3rd century',
    description:
      'Follow the Classis Britannica from the Channel ports to the Wealden ironworks, Hadrian’s Wall and Agricola’s northern campaigns—and discover why control of Britain depended upon control of the sea.',
    href: '/investigations/sea-roads-of-roman-britain.html',
    tags: ['Classis Britannica', 'Supply', 'Caledonia', 'Maritime power'],
    visualTitle: 'The Fleet Behind the Province',
    visualSubtitle: 'Channel · Coasts · Caledonia',
    buttonLabel: 'Follow the sea roads',
    icon: 'route',
  },
  {
    id: 'island-that-made-emperors',
    label: 'Featured Investigation',
    title: 'The Island That Made Emperors',
    date: 'AD 193–407',
    description:
      'Trace how Britain’s armies, ports and Channel crossings enabled commanders to seize power, defend breakaway regimes and carry imperial ambition into Gaul.',
    href: '/investigations/the-island-that-made-emperors.html',
    tags: ['Usurpers', 'Carausius', 'Military power', 'Imperial crisis'],
    visualTitle: 'The Army Behind the Throne',
    visualSubtitle: 'Acclamation · Rebellion · Departure',
    buttonLabel: 'Investigate the imperial claimants',
    icon: 'landmark',
  },
];

const InvestigationIcon = ({ type, className = '' }) => {
  const shared = `h-14 w-14 ${className}`;

  if (type === 'dna') return <Dna className={`${shared} text-[#c9a84c]`} />;
  if (type === 'route') return <Route className={`${shared} text-[#c9a84c]`} />;
  if (type === 'radar') return <Radar className={`${shared} text-[#78c8c3]`} />;
  if (type === 'trees') return <Trees className={`${shared} text-[#8fb67a]`} />;
  if (type === 'inscription') {
    return <ScrollText className={`${shared} text-[#d7bd70]`} />;
  }
  if (type === 'map-pinned') {
    return <MapPinned className={`${shared} text-[#d7bd70]`} />;
  }
  if (type === 'landmark') {
    return <Landmark className={`${shared} text-[#d7bd70]`} />;
  }

  return <Map className={`${shared} text-[#c9a84c]`} />;
};

const Investigations = () => {
  const [flippedId, setFlippedId] = useState(null);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setFlippedId(null);
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, []);

  const toggleCard = (id) => {
    setFlippedId((current) => (current === id ? null : id));
  };

  return (
    <main className="min-h-screen bg-[#0d0c0b] text-[#d4cfc5]">
      <section className="border-b border-[#c9a84c]/20 bg-gradient-to-b from-[#1b150c] to-[#0d0c0b] px-5 py-14 text-center sm:py-16">
        <div className="mx-auto max-w-4xl">
          <Landmark className="mx-auto mb-4 h-10 w-10 text-[#c9a84c]" />
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.28em] text-[#c9a84c]">
            Roads · Ruins · Memory
          </p>
          <h1 className="mb-5 font-serif text-3xl font-bold text-[#f0ece3] sm:text-4xl md:text-5xl">
            Featured Investigations
          </h1>
          <p className="mx-auto max-w-3xl text-base leading-7 text-[#b9b2a6] sm:text-lg">
            Immersive studies combining historical testimony, archaeology,
            landscape evidence and the newest scientific research.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 sm:py-14">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-center gap-3">
            <ScrollText className="h-6 w-6 text-[#c9a84c]" />
            <h2 className="font-serif text-2xl font-semibold text-[#f0ece3]">
              Current investigations
            </h2>
          </div>

          <p className="text-sm text-[#9f978b]">
            Tap or select a card to reveal its investigation.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {investigations.map((item) => {
            const isFlipped = flippedId === item.id;
            const detailsId = `${item.id}-details`;

            return (
              <article
                key={item.id}
                className="h-[430px] w-full"
                style={{ perspective: '1200px' }}
              >
                <div
                  className="relative h-full w-full transition-transform duration-700 motion-reduce:transition-none"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: isFlipped
                      ? 'rotateY(180deg)'
                      : 'rotateY(0deg)',
                  }}
                >
                  {/* Front */}
                  <div
                    className="absolute inset-0 overflow-hidden rounded-md border border-[#c9a84c]/35 bg-gradient-to-br from-[#2b1b0b] via-[#17140f] to-[#0d0c0b] shadow-2xl"
                    style={{ backfaceVisibility: 'hidden' }}
                    aria-hidden={isFlipped}
                  >
                    <button
                      type="button"
                      onClick={() => toggleCard(item.id)}
                      aria-expanded={isFlipped}
                      aria-controls={detailsId}
                      aria-label={`Show details for ${item.title}`}
                      tabIndex={isFlipped ? -1 : 0}
                      className="flex h-full w-full flex-col items-center justify-center px-7 py-8 text-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#e8c96e]"
                    >
                      <InvestigationIcon
                        type={item.icon}
                        className="mb-6"
                      />

                      <p className="font-serif text-2xl font-semibold leading-tight text-[#f0ece3] sm:text-3xl">
                        {item.visualTitle}
                      </p>

                      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#b2913e] sm:text-sm">
                        {item.visualSubtitle}
                      </p>

                      <span className="mt-auto inline-flex items-center gap-2 border-t border-[#c9a84c]/20 pt-5 text-sm font-semibold text-[#d7bd70]">
                        Tap to reveal
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </button>
                  </div>

                  {/* Reverse */}
                  <div
                    id={detailsId}
                    className="absolute inset-0 flex min-h-0 flex-col overflow-y-auto rounded-md border border-[#c9a84c]/45 bg-[#171510] p-5 shadow-2xl sm:p-6"
                    style={{
                      backfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                    }}
                    aria-hidden={!isFlipped}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#c9a84c]">
                        {item.label}
                      </p>

                      <button
                        type="button"
                        onClick={() => toggleCard(item.id)}
                        tabIndex={isFlipped ? 0 : -1}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[#c9a84c]/25 px-2.5 py-1.5 text-xs font-semibold text-[#d7bd70] transition hover:bg-[#c9a84c]/10 focus:outline-none focus:ring-2 focus:ring-[#e8c96e]"
                        aria-label={`Show front of ${item.title}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Back
                      </button>
                    </div>

                    <h2 className="mt-4 font-serif text-[1.65rem] font-bold leading-tight text-[#f0ece3]">
                      {item.title}
                    </h2>

                    <p className="mt-2 text-sm font-medium text-[#b59a50]">
                      {item.date}
                    </p>

                    <p className="mt-4 text-sm leading-6 text-[#c8c1b6]">
                      {item.description}
                    </p>

                    <div className="mt-5 flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-sm border border-[#c9a84c]/25 bg-[#c9a84c]/5 px-2 py-1 text-xs text-[#d7bd70]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <a
                      href={item.href}
                      tabIndex={isFlipped ? 0 : -1}
                      className="mt-auto inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#e8c96e] transition hover:bg-[#c9a84c] hover:text-[#0d0c0b] focus:outline-none focus:ring-2 focus:ring-[#e8c96e] focus:ring-offset-2 focus:ring-offset-[#171510]"
                    >
                      {item.buttonLabel}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-12 rounded-md border border-[#c9a84c]/15 bg-[#141210] p-7 text-center">
          <p className="font-serif text-xl text-[#e5ded2]">
            Each investigation distinguishes documented evidence from interpretation.
          </p>
          <p className="mt-2 text-[#8f897f]">
            Campaigns, peoples and hidden landscapes—different methods, one
            purpose: to bring Roman Britain close without making it false.
          </p>
        </div>
      </section>
    </main>
  );
};

export default Investigations;
